import { ForwardedRef, forwardRef, Key, LiHTMLAttributes, useId } from "react";

import {
  Box,
  FormHelperText,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  SelectProps,
  SvgIconProps,
} from "@mui/material";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { i18n } from "translations";
import { rem } from "utils";

import { useMultiselect } from "./Multiselect.hooks";
import {
  Container,
  SelectedIconWrapper,
  StylesSelect,
} from "./Multiselect.styles";
import { MultiselectProps, MultiselectReference } from "./Multiselect.types";
import Divider from "../Divider/Divider";
import { CheckIcon, ChevronDownIcon } from "../Icon";
import InputLabel from "../InputLabel/InputLabel";
import Typography from "../Typography/Typography";

const IconComponent = (props: SvgIconProps) => (
  <ChevronDownIcon {...props} fontSize="small" />
);

const MultiselectInner = <V, R>(
  {
    defaultValue,
    displayEmpty = true,
    fullWidth = true,
    helperText,
    label,
    options,
    placeholder,
    value,
    noOptionsText = "No options",
    tooltipText,
    renderOption,
    renderValue,
    onChange,
    ...innerProps
  }: MultiselectProps<V>,
  ref: ForwardedRef<R>
) => {
  const { multiselectRef, valueState, onChangeHandler, onSelectAllHandler } =
    useMultiselect({
      ref,
      options,
      defaultValue,
      value,
      onChange,
    });

  const formId = useId();

  return (
    <Container id={innerProps.id}>
      {!!label && (
        <InputLabel
          htmlFor={innerProps.id ?? formId}
          required={innerProps.required}
          helperText={tooltipText}
        >
          {label}
        </InputLabel>
      )}
      <StylesSelect
        {...innerProps}
        ref={multiselectRef}
        multiple
        size="small"
        variant="outlined"
        fullWidth={fullWidth}
        displayEmpty={displayEmpty}
        value={valueState ?? null}
        labelId={innerProps.id ?? formId}
        input={<OutlinedInput id={innerProps.id ?? formId} />}
        IconComponent={IconComponent}
        onChange={
          onChangeHandler as (event: SelectChangeEvent<unknown>) => void
        }
        renderValue={
          (renderValue as unknown as SelectProps<unknown>["renderValue"]) ??
          (valueState.length === 0 ? () => placeholder : undefined)
        }
        MenuProps={{
          disableAutoFocusItem: true,
          sx: {
            ".MuiPaper-root": {
              boxShadow: shadows.lg,
            },
          },
          MenuListProps: {
            sx: {
              padding: `${rem(4)} ${rem(6)} !important`,
              li: {
                justifyContent: "space-between",
                alignItems: "center",
                padding: `${rem(8)} ${rem(6)}`,
                borderRadius: "6px",
                "&:hover": {
                  backgroundColor: gray[50],
                },
                "&.Mui-selected": {
                  backgroundColor: gray[50],
                  "&:hover": { backgroundColor: gray[50] },
                },
              },
              "li + li": {
                marginTop: rem(4),
              },
            },
          },
        }}
      >
        {options.length === 0 ? (
          <MenuItem disabled>{noOptionsText}</MenuItem>
        ) : (
          <Box>
            {/* NOTE: The command line "e.stopPropagation()" use to prevent Select's "onChange" event */}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                onSelectAllHandler(!(options.length === valueState.length));
              }}
            >
              <Typography display="inline-block" fontWeight={500} type="TextSM">
                {i18n.t("selectAll")}
              </Typography>
              {options.length === valueState.length ? (
                <SelectedIconWrapper className="selected-icon-wrapper">
                  <CheckIcon color="primary" fontSize="small" />
                </SelectedIconWrapper>
              ) : null}
            </MenuItem>
            <Divider />
          </Box>
        )}
        {options.map((i) => (
          <MenuItem
            key={i.value as Key | null | undefined}
            value={i.value as LiHTMLAttributes<V>["value"]}
          >
            {typeof renderOption === "function" ? (
              renderOption(i)
            ) : (
              <Typography display="inline-block" type="TextSM">
                {i.label}
              </Typography>
            )}
            {valueState.indexOf(i.value) > -1 ? (
              <SelectedIconWrapper className="selected-icon-wrapper">
                <CheckIcon color="primary" fontSize="small" />
              </SelectedIconWrapper>
            ) : null}
          </MenuItem>
        ))}
      </StylesSelect>
      {helperText && (
        <FormHelperText error={innerProps.error}>{helperText}</FormHelperText>
      )}
    </Container>
  );
};

const Multiselect = forwardRef(MultiselectInner) as <V>(
  props: MultiselectProps<V> & { ref?: ForwardedRef<MultiselectReference<V>> }
) => ReturnType<typeof MultiselectInner>;

export default Multiselect;
